import * as React from "react";
import Box from "@mui/material/Box";
import MuiCard from "@mui/material/Card";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormLabel from "@mui/material/FormLabel";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import LoginIcon from "@mui/icons-material/Login";
import FormControl from "@mui/material/FormControl";
import InputAdornment from "@mui/material/InputAdornment";
import { useAuth } from "../../../context/AuthContext.tsx";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import { styled, useColorScheme } from "@mui/material/styles";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

const Card = styled(MuiCard)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignSelf: "center",
  width: "100%",
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  boxShadow:
    "hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px",
  [theme.breakpoints.up("sm")]: {
    width: "450px",
  },
  ...theme.applyStyles("dark", {
    boxShadow:
      "hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px",
  }),
}));

export default function SignInCard() {
  const { login } = useAuth();
  const [isLoading, setIsLoading] = React.useState(false);
  const [rememberMe, setRememberMe] = React.useState(false);
  const [cpfCnpjValue, setCpfCnpjValue] = React.useState("");
  const [cpfCnpjError, setCpfCnpjError] = React.useState(false);
  const [cpfCnpjErrorMessage, setCpfCnpjErrorMessage] = React.useState("");
  const [passwordError, setPasswordError] = React.useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);

  React.useEffect(() => {
    const rememberCnpj = localStorage.getItem("rememberedCnpj");
    if (rememberCnpj) {
      setCpfCnpjValue(rememberCnpj);
      setRememberMe(true);
    }
  }, []);

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);

    if (!validateInputs()) {
      setIsLoading(false);
      return;
    }

    const formData = new FormData(event.currentTarget);
    const cpfCnpj = formData.get("cpfCnpj") as string;
    const password = formData.get("password") as string;

    setCpfCnpjValue(cpfCnpj);

    try {
      await login(cpfCnpj, password);

      if (rememberMe) {
        localStorage.setItem("rememberedCnpj", cpfCnpj);
      } else {
        localStorage.removeItem("rememberedCnpj");
      }

      setPasswordError(false);
      setPasswordErrorMessage("");
    } catch (error: any) {
      setPasswordError(true);
      setPasswordErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  const validateInputs = () => {
    const cpfCnpj = document.getElementById("cpfCnpj") as HTMLInputElement;
    const password = document.getElementById("password") as HTMLInputElement;

    let isValid = true;

    const cleanedCpfCnpj = cpfCnpj.value.replace(/\D/g, "");

    if (
      !cleanedCpfCnpj ||
      !(cleanedCpfCnpj.length === 11 || cleanedCpfCnpj.length === 14)
    ) {
      setCpfCnpjError(true);
      setCpfCnpjErrorMessage(
        "Informe um CPF (11 dígitos) ou CNPJ (14 dígitos) válido."
      );
      isValid = false;
    } else {
      setCpfCnpjError(false);
      setCpfCnpjErrorMessage("");
    }

    if (!password.value || password.value.length < 6) {
      setPasswordError(true);
      setPasswordErrorMessage("Sua senha deve ter pelo menos 6 caracteres.");
      isValid = false;
    } else {
      setPasswordError(false);
      setPasswordErrorMessage("");
    }

    return isValid;
  };

  const scheme = useColorScheme();
  const isLightMode = scheme.mode === "light";
  const contrastColor = isLightMode ? "#fff" : "#000";

  return (
    <Card variant="outlined">
      <Typography
        component="h1"
        variant="h4"
        sx={{ width: "100%", fontSize: "clamp(2rem, 10vw, 2.15rem)" }}
      >
        Entrar
      </Typography>
      <Box
        component="form"
        onSubmit={handleSubmit}
        method="POST"
        noValidate
        sx={{ display: "flex", flexDirection: "column", width: "100%", gap: 2 }}
      >
        <FormControl>
          <FormLabel htmlFor="cpfCnpj">CNPJ:</FormLabel>
          <TextField
            error={cpfCnpjError}
            helperText={cpfCnpjErrorMessage}
            id="cpfCnpj"
            name="cpfCnpj"
            placeholder="Digite seu CPF ou CNPJ"
            autoComplete="off"
            autoFocus
            required
            variant="outlined"
            color={cpfCnpjError ? "error" : "primary"}
            inputProps={{ maxLength: 18 }}
            value={cpfCnpjValue}
            onChange={(e) => setCpfCnpjValue(e.target.value)}
          />
        </FormControl>

        {/* CAMPO DE SENHA COM VISIBILIDADE */}
        <FormControl>
          <FormLabel htmlFor="password">SENHA:</FormLabel>
          <TextField
            error={passwordError}
            helperText={passwordErrorMessage}
            name="password"
            placeholder="••••••"
            type={showPassword ? "text" : "password"}
            id="password"
            autoComplete="current-password"
            required
            fullWidth
            variant="outlined"
            color={passwordError ? "error" : "primary"}
            inputProps={{ maxLength: 30 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                    aria-label="mostrar ou ocultar senha"
                    style={{ border: "none", background: "none"}}
                  >
                    {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </FormControl>

        <FormControlLabel
          control={
            <Checkbox
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              color="primary"
            />
          }
          label="Lembrar de mim"
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={isLoading}
          startIcon={
            isLoading ? (
              <CircularProgress size="30px" sx={{ mr: 1, color: contrastColor }} />
            ) : (
              <LoginIcon sx={{ mr: 1, color: contrastColor }} />
            )
          }
        >
          <Typography sx={{ color: contrastColor }} fontWeight="bold">
            {isLoading ? "Entrando..." : "Entrar"}
          </Typography>
        </Button>
      </Box>
    </Card>
  );
}
